import EmailIcon from '@material-design-icons/svg/filled/email.svg?react';
import { isLeft } from 'fp-ts/lib/Either';
import { useEffect, useRef, useState } from 'react';
import { Navigate } from 'react-router';
import { toast } from 'sonner';

import { AuthStateType, useAuth } from '../../hooks/useAuth';
import { usePageTitle } from '../../hooks/usePageTitle';
import { useTRPC } from '../../hooks/useTRPC';
import { unknownToLeft } from '../../lib/unknownToLeft';
import { captureAndReportError } from '../../utils/errorReporting';
import { Button } from '../Button';
import { LayoutWithBgGradient } from '../LayoutWithBgGradient';
import { PageHeader } from '../PageHeader';
import { VerificationCodeInput } from '../VerificationCodeInput';

interface Props {
  className?: string;
}

export function EmailVerificationPage(props: Props) {
  const [auth, { signOut, setOnboardingState }] = useAuth();
  const trpc = useTRPC();
  const email =
    auth.type === AuthStateType.Authenticated ? auth.user.email : '';
  const [verificationCode, setVerificationCode] = useState('');
  const [error, setError] = useState('');
  const [resendError, setResendError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [countdownSeconds, setCountdownSeconds] = useState(0);
  const initialEmailSentRef = useRef(false);

  usePageTitle('Verify Email | Publishing Portal');

  useEffect(() => {
    if (countdownSeconds > 0) {
      const timer = setTimeout(() => {
        setCountdownSeconds((prev) => prev - 1);
      }, 1000);
      return () => clearTimeout(timer);
    } else if (countdownSeconds === 0 && resendError.includes('Please wait')) {
      setResendError('');
    }
  }, [countdownSeconds, resendError]);

  const sendVerification = async (isResend = false) => {
    setResendLoading(true);
    setResendError('');
    setCountdownSeconds(0);

    const result = await trpc.sendEmailVerification
      .mutate(email)
      .catch(unknownToLeft);

    setResendLoading(false);

    if (isLeft(result)) {
      const errorMessage = result.left.message;

      if (errorMessage.includes('Please wait')) {
        const match = errorMessage.match(/(\d+) seconds/);
        if (match) {
          const seconds = parseInt(match[1], 10);
          setCountdownSeconds(seconds);
        }
        setResendError(errorMessage);
        if (isResend) {
          toast.error('Too soon to resend', {
            description: errorMessage,
            duration: 4000,
          });
        }
      } else {
        const message = 'Failed to send verification email. Please try again.';
        setResendError(message);
        if (isResend) {
          toast.error('Failed to send email', {
            description: message,
            duration: 4000,
          });
        }
      }
      captureAndReportError(result);
    } else {
      setResendError('');
      setCountdownSeconds(0);
      if (isResend) {
        toast.success('Verification email sent!', {
          description: `A new verification code has been sent to ${email}`,
          duration: 5000,
        });
      }
    }
  };

  useEffect(() => {
    // Prevent duplicate emails in StrictMode by tracking if initial email was sent
    if (!initialEmailSentRef.current) {
      initialEmailSentRef.current = true;
      sendVerification();
    }
  }, []);

  const isResendDisabled = resendLoading || countdownSeconds > 0;

  const getResendButtonText = () => {
    if (resendLoading) return 'Sending...';
    if (countdownSeconds > 0) return `Wait ${countdownSeconds}s`;
    return 'Resend code';
  };

  if (!email) {
    return (
      <LayoutWithBgGradient className={props.className}>
        <div className="h-screen w-screen">
          <Navigate to="/auth" />
        </div>
      </LayoutWithBgGradient>
    );
  }

  return (
    <LayoutWithBgGradient className={props.className}>
      <div className="h-screen w-screen overflow-y-auto">
        <PageHeader />
        <div className="flex flex-col items-center mt-10">
          <div className="flex flex-col items-center w-full max-w-[640px] px-20">
            <div className="text-4xl text-white">Enter your code</div>
            <div className="text-sm text-white mt-2">Your code was sent to</div>
            <div className="text-sm text-white">{email}</div>
            <div className="mt-2 flex flex-col items-center">
              <button
                type="button"
                className="text-sm text-[rgba(43,136,143,1)] disabled:opacity-50 disabled:cursor-not-allowed"
                onClick={() => sendVerification(true)}
                disabled={isResendDisabled}
              >
                {getResendButtonText()}
              </button>
              {resendError && countdownSeconds === 0 && (
                <div className="text-xs text-rose-500 mt-1 max-w-sm text-center">
                  {resendError}
                </div>
              )}
            </div>
            <VerificationCodeInput
              className="mt-12"
              value={verificationCode}
              onChange={setVerificationCode}
              autoFocus
            />
            <div className="mt-6 w-full">
              <Button
                className="w-full"
                disabled={verificationCode.length !== 6}
                onClick={async () => {
                  setSubmitting(true);
                  setError('');

                  const result = await trpc.verifyEmailWithCode
                    .mutate({ email, code: verificationCode })
                    .catch(unknownToLeft);

                  if (isLeft(result)) {
                    setError(result.left.message);
                    toast.error('Verification failed', {
                      description: result.left.message,
                      duration: 4000,
                    });
                  } else {
                    toast.success('Email verified!', {
                      description: 'Your email has been successfully verified.',
                      duration: 3000,
                    });
                    setOnboardingState('ProfileCreation');
                  }

                  setSubmitting(false);
                }}
                submitting={submitting}
              >
                Create Account
              </Button>
            </div>
            {error && <div className="text-rose-500 text-xs mt-2">{error}</div>}
            <div className="mt-12">
              <button
                type="button"
                className="flex items-center text-neutral-400 text-xs"
                onClick={() => signOut()}
              >
                <EmailIcon className="size-4 mr-1.5 fill-current" />
                <div>Create an account with a different email</div>
              </button>
            </div>
          </div>
        </div>
      </div>
    </LayoutWithBgGradient>
  );
}
